<template>
    <abstract-search>

        <template #filter>

            <filter-menu-group
                name="Order Status"
                unique-id="orders.order_status"
                :options="baseOrderStatuses"
                :selected-ids="genericFilters.orderStatusId"
                :initially-collapsed="false"
                v-on:selected="applyFilterStatus('Order Status', statusUtil.ORDER, 'orderStatusId', $event)"
            >
            </filter-menu-group>

            <filter-menu-group
                name="Invoice Status"
                unique-id="orders.payment_status"
                :options="basePaymentStatuses"
                :selected-ids="genericFilters.paymentStatusId"
                :initially-collapsed="false"
                v-on:selected="applyFilterStatus('Payment Status', statusUtil.PAYMENT, 'paymentStatusId', $event)"
            >
            </filter-menu-group>

            <filter-menu-group
                name="Purchase Order Status"
                unique-id="orders.purchase_order_status"
                :options="basePurchaseOrderStatuses"
                :selected-ids="genericFilters.purchaseOrderStatusId"
                :initially-collapsed="true"
                v-on:selected="applyFilterStatus('Purchase Order Status', statusUtil.PURCHASE_ORDER, 'purchaseOrderStatusId', $event)"
            >
            </filter-menu-group>

            <filter-menu-group
                name="Fulfillment Status"
                unique-id="orders.fulfillment_status"
                :options="baseFulfillmentStatuses"
                :selected-ids="genericFilters.fulfillmentStatusId"
                :initially-collapsed="true"
                v-on:selected="applyFilterStatus('Fulfillment Status', statusUtil.FULFILLMENT, 'fulfillmentStatusId', $event)"
            >
            </filter-menu-group>

        </template>

        <template #after-search>
            <div class="is-narrow">
                <div class="flex-con sm-pad-lr">
                    <div>
                        <search-date-picker
                            v-model="genericFilters.startDate"
                            @input="fetchItems(true)"
                            label="Start Date"
                            :with-time="dateWithTimeStates.startDate"
                            @with-time-toggle="dateWithTimeStates.startDate = $event"
                        >
                        </search-date-picker>
                    </div>
                    <div>
                        <search-date-picker
                            v-model="genericFilters.endDate"
                            @input="fetchItems(true)"
                            label="End Date"
                            :with-time="dateWithTimeStates.endDate"
                            @with-time-toggle="dateWithTimeStates.endDate = $event"
                        >
                        </search-date-picker>
                    </div>

                </div>
            </div>
        </template>

        <template #th-start>
            <th class="status-col" scope="col">Status</th>
            <th class="progress-col is-skinny" scope="col">Progress</th>
        </template>

        <template v-slot:tr-start="{ item : order }">
            <td class="status-col">
                <span
                    class="tag status-tag"
                    :class="'order-status-bg-color-' + order.orderStatusId"
                >{{order.orderStatus.name}}</span>
            </td>
            <td class="progress-col is-skinny-no-wrap">
                <all-order-status-icons :order="order"></all-order-status-icons>
            </td>
        </template>

        <template v-slot:default-search-modal-body>
            <p>By default searches will look for matches against these data points:</p>
            <div class="content">
                <ul>
                    <li>Order #</li>
                    <li>Order Reference #</li>
                    <li>Invoice #</li>
                    <li>Purchase Order #</li>
                    <li>Ship To Name</li>
                    <li>Ship To Address 1 & 2</li>
                    <li>Line Item SKUs</li>
                    <li>Line Item Titles</li>
                </ul>
            </div>
        </template>

        <modal
            :active="exportModal.active"
            :close="() => {exportModal.active = false}"
        >
            <div class="has-text-centered">
                <h2
                    class="is-size-5"
                    style="padding:0 80px"
                >Would you like to export all pages of orders matching your current filters or only the current page?</h2>
            </div>
            <div class="buttons is-centered m-t-md">
                <button
                    class="button"
                    @click="exportOrders(true)"
                >
                    Current Page
                </button>
                <button
                    class="button"
                    @click="exportOrders(false)"
                >
                    All Pages
                </button>
            </div>
        </modal>

    </abstract-search>
</template>

<script>

import AbstractSearchMixin from '../abstract-search/AbstractSearchMixin';

import OrderSearchApi from '../../api/OrderSearchApi';
import OrderFieldsUtil from '../orders/util/OrderFieldsUtil';
import StatusUtil from '../../util/StatusUtil';

import AbstractSearch from '../../components/abstract-search/AbstractSearch';
import FilterMenuGroup from '../abstract-search/components/FilterMenuGroup';
import FilterList from '../abstract-search/filters/FilterList';
import SearchDatePicker from '../abstract-search/components/SearchDatePicker';

import AllOrderStatusIcons from "../order-progress/AllOrderStatusIcons";
import BulkActionsButton from "../abstract-search/components/BulkActionsButton";
import NumberUtil from "../../util/NumberUtil";

export default {
    name : "ViewAllOrders",
    mixins : [AbstractSearchMixin],
    components : {
        AllOrderStatusIcons,
        AbstractSearch,
        FilterMenuGroup,
        FilterList,
        SearchDatePicker,
        BulkActionsButton,
    },
    data () {
        return {

            //start required by mixin
            searchCacheKey : 'orders',
            availableFields : OrderFieldsUtil,
            apiSearchMethod : OrderSearchApi.search,
            defaultSortColumnHandle : 'order.id',
            defaultProjectionColumns : [
                'order.ordered_at',
                'order.id',
                'order.reference_number',
                'shipping_address.name',
                'order.total_order_items',
                'order.order_total',
            ],
            //end required by mixin

            baseOrderStatuses : Object.values(StatusUtil.ORDER),
            //if the dealer is in a channel which doesn't collect payment only show the relevant statuses
            basePaymentStatuses : Object.values(
                this.$getSessionContext().paymentCollection
                    ? StatusUtil.PAYMENT
                    : Object.values(StatusUtil.PAYMENT).filter(s => s.id === StatusUtil.PAYMENT.PAID.id || s.id === StatusUtil.PAYMENT.DEFERRED.id)
            ),
            basePurchaseOrderStatuses : Object.values(StatusUtil.PURCHASE_ORDER),
            baseFulfillmentStatuses : Object.values(StatusUtil.FULFILLMENT),

            dateWithTimeStates : {
                startDate : false,
                endDate : false,
            },

            genericFilters : {
                dealerId : this.dealerId,
                endDate : null,
                startDate : null,
                orderStatusId : [],
                paymentStatusId : [],
                purchaseOrderStatusId : [],
                fulfillmentStatusId : [],
                invoiceStatusId : [],
            },

            groupBulkCaptureModal : {
                active : false,
            },

            exportModal : {
                active : false,
                loading : false,
            },

            channelInvoiceModal : {
                active : false,
                notification : {},
            },

            bulkActionItemNoun : 'orders',
            bulkActionExportMethodOverride : () => {
                this.exportModal.active = true;
            },
            bulkActionItems : [
                {
                    name : 'Authorize',
                    method : () => {
                        this.bulkAuthAction(true);
                    },
                },
                {
                    name : 'Un-Authorize',
                    method : () => {
                        this.bulkAuthAction(false);
                    },
                },
                {
                    name : 'Cancel',
                    method : this.bulkCancel,
                },
            ],
            disabled : {
                itemIds : [],
                idFieldName : 'id',
                rowClasses : 'disabled-row-gray1',
                rowTooltip : 'Draft orders cannot be selected',
            },

        };
    },
    computed : {
        statusUtil () {
            return StatusUtil;
        },
    },
    methods : {

        searchCacheApplier (context) {
            if(context.dateWithTimeStates) {
                this.dateWithTimeStates = context.dateWithTimeStates;
            }
        },

        searchCacheAugmenter () {
            return {
                dateWithTimeStates : this.dateWithTimeStates,
            };
        },

        searchResultItemsTransformer (data) {
            data.items.forEach(order => {
                order.orderStatus = StatusUtil.ORDER.findById(order.orderStatusId);
            });
            return data.items;
        },

        applyFilterStatus (statusName, statusClass, filterKey, context) {

            const status = statusClass.findById(context.option.id);
            const label = `${statusName} : ${status.name}`;

            this.addGenericFilter(
                context.addToSelection,
                label,
                statusName,
                filterKey,
                context.option.id
            );

        },

        onItemRowClick (order) {
            if(order.orderStatusId === StatusUtil.ORDER.DRAFT.id){
                return;
            }else if(!this.dealerId){
                this.$router.push({ name : this.$ROUTES.ORDERS_VIEW, params : { id : order.id }});
            } else {
                this.$router.push({ name : this.$ROUTES.DEALER_ORDERS_VIEW, params : { dealerId : this.dealerId, id : order.id }});
            }
        },

        exportOrders (currentPageOnly) {
            this.exportModal.active = false;
            const request = this.getFetchRequestContextMassaged();
            request.disablePagination = !currentPageOnly;
            OrderSearchApi.exportOrders('orders-export.csv', {}, { method : 'POST', data : request }).finally(() => {
                this.exportModal.loading = false;
            });
        },

        bulkAuthAction (auth) {
            const verb = auth ? 'authorize' : 'unauthorize';
            this.validateBulkAction(`orders to ${verb}`).then(request => {
                if(auth) {
                    request.bulkAuthorize = true;
                } else {
                    request.bulkUnAuthorize = true;
                }
                this.openConfirmActionModal(
                    `Are you sure you want to ${verb} all eligible orders of the ${request.bulkActionTotalItems} you have selected?`,
                    () => {
                        this.confirmActionModal.loading = true;
                        return OrderSearchApi.bulkAuthAction(request).then(response => {
                            this.confirmActionModal.loading = false;
                            const added = NumberUtil.withCommas(response.data.numberEffected);
                            this.$notifySuccess(`${added} Orders have been ${verb}d`);
                            this.unSelectAllAndReFetch();
                        })
                    });
            });
        },

        bulkCancel () {
            this.validateBulkAction(`orders to cancel`).then(request => {
                this.openConfirmActionModal(
                    `Are you sure you want to cancel all eligible orders of the ${request.bulkActionTotalItems} you have selected?`,
                    () => {
                        this.confirmActionModal.loading = true;
                        return OrderSearchApi.bulkCancel(request).then(response => {
                            this.confirmActionModal.loading = false;
                            const added = NumberUtil.withCommas(response.data.numberEffected);
                            this.$notifySuccess(`${added} Orders have been canceled`);
                            this.unSelectAllAndReFetch();
                        })
                    });
            });
        },
        seedDisabledItems (items) {
            this.disabled.itemIds = items.filter(item => StatusUtil.ORDER.DRAFT.id === item.orderStatusId).map(i=>i.id);
        }
    },
}
</script>

<style lang="scss" scoped>

    .progress-col {
        .flex-con > div {
            flex:0 0 22px;
        }
        /deep/ .fa {
            font-size:15px;
            line-height:20px;
        }
    }


</style>
